import { useIntl } from 'react-intl';
import { Button, Col, Divider, Form, Modal, Row, Tooltip, Space } from 'antd';

import { Helmet } from 'react-helmet';

import { PageProps } from '@/types/PageProps';

import PurchasingGroupingForm from '@/components/Movements/PurchasingGrouping/PurchasingGroupingForm';
import {
  DefaultRouteTabs,
  DocumentTypesInfiniteSelectField,
  FloatingFormItem,
  SaveButton,
} from '@/components/_common';
import {
  PurchaseGroupingFormValues,
  PurchaseGroupingQuery,
} from '@/core/domain/purchase/PurchaseGroup/PurchaseGroup.types';
import { useState } from 'react';
import {
  Purchase,
  PurchaseGroupCreate,
} from '@/core/domain/purchase/Purchase.types';
import { usePurchaseGroupingGenerate } from '@/core/domain/purchase/Purchase.hooks';
import {
  useDefaultEmptyFieldError,
  useDisclosure,
  useWindowSize,
} from '@/hooks';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';

function PurchasingGroupingPage({ route }: PageProps) {
  const intl = useIntl();
  const { isMobile } = useWindowSize();
  const [purchasesSelected, setPurchasesSelected] = useState<Purchase[]>();
  const [purchaseCreated, setPurchasesCreated] = useState<Purchase>();
  const [modalConfirm, modalConfirmAction] = useDisclosure();

  const defaultEmptyFieldError = useDefaultEmptyFieldError();

  const [queryParams, setQueryParams] = useState<PurchaseGroupingQuery>({
    order: 'DESC',
    ...convertParams<PurchaseGroupingQuery>({
      companyId: 'number',
      supplierId: 'number',
      supplierName: 'string',
      orderByColumn: 'string',
    }),
  });

  usePersistSearchParams(queryParams);

  const [form] = Form.useForm<PurchaseGroupingFormValues>();

  const totalItens = purchasesSelected?.reduce(
    (acc, curr) => (acc += curr.totalAmount),
    0,
  );

  const handlerSearchPurchases = async () => {
    const { supplier, ...values } = await form.validateFields();

    setQueryParams((prev) => ({
      ...prev,
      ...values,
      supplierId: supplier.id,
      supplierName: supplier.name,
    }));
  };

  const createGroupMutation = usePurchaseGroupingGenerate();

  const handleCreatePurchaseGroup = async (values?: Purchase[]) => {
    const { documentTypeId } = await form.validateFields();

    if (values && documentTypeId) {
      const obj: PurchaseGroupCreate = {
        documentTypeId,
        body: values.map((item) => ({
          ...item,
          departureDate: item.departureDate,
        })),
      };

      const response = await createGroupMutation.mutateAsync(obj);

      setPurchasesCreated(response);

      modalConfirmAction.close();
      form.resetFields(['documentTypeId']);
    }
  };

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <DefaultRouteTabs
        persistSearchParams={false}
        route={route.parent}
        permissions={route.permissions}
      >
        <PurchasingGroupingForm
          form={form}
          totalItens={totalItens}
          callback={handlerSearchPurchases}
          queryParams={queryParams}
          setQueryParams={setQueryParams}
          setPurchasesSelected={setPurchasesSelected}
          purchaseCreated={purchaseCreated}
          handlePurchaseCreatedReset={() => setPurchasesCreated(undefined)}
        />

        {!purchaseCreated && (
          <Row>
            <Divider />

            <Col span={isMobile ? 24 : undefined}>
              <Tooltip
                title={
                  (purchasesSelected?.length ?? 0) < 2
                    ? 'Selecione mais de uma compra para gerar o agrupamento'
                    : undefined
                }
              >
                <Button
                  block={isMobile}
                  type="primary"
                  onClick={modalConfirmAction.open}
                  disabled={(purchasesSelected?.length ?? 0) < 2}
                >
                  {intl.formatMessage({
                    id: 'purchasingGrouping.form.button.generateCluster',
                  })}
                </Button>
              </Tooltip>
            </Col>
          </Row>
        )}
      </DefaultRouteTabs>

      <Modal
        title={'Confirmação do agrupamento'}
        open={modalConfirm}
        onCancel={() => {
          form.resetFields(['documentTypeId']);
          modalConfirmAction.close();
        }}
        footer={[
          <Space>
            <Button
              onClick={() => {
                form.resetFields(['documentTypeId']);
                modalConfirmAction.close();
              }}
            >
              {intl.formatMessage({ id: 'commons.form.button.close' })}
            </Button>
            <SaveButton
              onClick={() => handleCreatePurchaseGroup(purchasesSelected)}
              loading={createGroupMutation.isLoading}
            >
              Gerar
            </SaveButton>
          </Space>,
        ]}
      >
        <Form form={form}>
          <Row gutter={[24, 24]}>
            <Col span={24}>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'purchasingGrouping.form.documentType',
                })}
                name="documentTypeId"
                rules={[defaultEmptyFieldError]}
              >
                <DocumentTypesInfiniteSelectField
                  queryFilter={{ active: true }}
                />
              </FloatingFormItem>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}

export default PurchasingGroupingPage;
